import { useLanguageLabelMap } from "PFApp/hooks/use_language_label_map";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { SelectV2 } from "PFComponents/select_v2";
import { isSaasAdmin } from "PFCore/helpers/profile";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { useProficiencyLevelsInvalidate } from "PFCore/hooks/queries/proficiency_levels/use_proficiency_levels_invalidate";
import { useCurrentProfile, useCurrentProfileUpdate } from "PFCore/hooks/queries/profile";
import { Option } from "PFTypes/options";
import { useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ProfileEditPanel from "../../profile_edit_panel";
import { getProfileSectionText } from "../../profile_edit_sections";
import css from "./laguage_settings.module.scss";

type LanguageForm = {
  locale: string | undefined;
};

export const LanguageSettingsSection = () => {
  const { t: profilesT } = useTranslation("profiles", { keyPrefix: "edit.sections" });
  const { t } = useTranslation();

  const {
    data: {
      customization: { allowedLocales, locale: defaultAccountLocale }
    }
  } = useCurrentAccount();
  const { invalidateProficiencyLevels } = useProficiencyLevelsInvalidate();
  const languageLabelMap = useLanguageLabelMap();

  const { data: currentProfile } = useCurrentProfile();

  const { locale } = currentProfile;

  const { showGrowl } = useGrowlStoreActions();
  const saasAdmin = isSaasAdmin(currentProfile);

  const { handleSubmit, control } = useForm<LanguageForm>({
    defaultValues: {
      locale
    }
  });

  const { mutate: updateProfile, isPending: isUpdateProfileLoading } = useCurrentProfileUpdate({
    onSuccess: () => {
      invalidateProficiencyLevels();
      showGrowl({
        message: profilesT("settingsUpdatedMessage")
      });
    }
  });

  const handleSave: SubmitHandler<LanguageForm> = (formData) => updateProfile(formData);

  const localeOptions = useMemo(
    () =>
      (allowedLocales ?? []).map((locale) => ({
        id: locale,
        value:
          locale === defaultAccountLocale
            ? profilesT("languageSettings.defaultLocale", {
                locale: languageLabelMap[locale]
              })
            : languageLabelMap[locale],
        original: locale,
        disabled: saasAdmin && locale !== "en"
      })),
    [allowedLocales, defaultAccountLocale, saasAdmin, languageLabelMap, profilesT]
  );

  return (
    <ProfileEditPanel title={getProfileSectionText("language_settings")}>
      <div className={css.root}>
        <form className={css.form}>
          <Controller
            control={control}
            name={"locale"}
            render={({ field: { onChange, value } }) => (
              <SelectV2
                label={profilesT("languageSettings.language")}
                tip={profilesT("languageSettings.languageHint")}
                style={{ width: "100%" }}
                value={value}
                options={localeOptions}
                onChange={(option: Option<string>) => onChange(option.original)}
              />
            )}
          />
        </form>

        <Button
          text={t("update")}
          className={css.button}
          kind="primary"
          disabled={isUpdateProfileLoading}
          onClick={handleSubmit(handleSave)}
        />
      </div>
    </ProfileEditPanel>
  );
};
